import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { useAuth, useNotificationsSocket } from "../lib/auth";
import { MessageSquare, BadgeCheck, Loader2 } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d`;
  return `${Math.floor(diff / 2592000)}mo`;
}

/**
 * ChatList — the sidebar list of DM threads. Used inside <ChatsShell />.
 * On desktop this is always visible on the left; on mobile it fills the whole
 * screen when at /chats and is hidden when a thread is open.
 */
export default function ChatList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { peer_id: activePeer } = useParams();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    api.get("/chats").then((r) => setThreads(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  // Realtime: reload on any new message notification
  useNotificationsSocket((n) => { if (n.kind === "message") load(); });

  // Also refetch counts whenever the active thread changes (marks-as-read effect)
  useEffect(() => { if (activePeer) load(); }, [activePeer]);

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 lg:px-5 pt-6 pb-4 border-b border-border">
        <div className="label-mono text-muted-foreground text-[11px] mb-1">Direct messages</div>
        <h1 className="font-serif text-3xl leading-tight">Chats</h1>
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
        </div>
      ) : threads.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center px-6 py-10">
          <MessageSquare className="w-10 h-10 mb-3 text-muted-foreground" />
          <div className="font-serif text-lg mb-1">No conversations yet</div>
          <div className="text-xs text-muted-foreground mb-4 max-w-[240px]">
            Share a debate or thought with someone you follow to start chatting.
          </div>
          <button
            onClick={() => navigate(`/profile/${user?.username}`)}
            className="text-xs font-semibold text-[hsl(var(--primary))] hover:underline"
            data-testid="chats-empty-cta"
          >Find someone to follow →</button>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto divide-y divide-border">
          {threads.map((t) => {
            if (!t.peer) return null;
            const unread = t.unread_count || 0;
            const active = t.peer.id === activePeer;
            return (
              <NavLink
                key={t.id}
                to={`/chats/${t.peer.id}`}
                className={`flex items-center gap-3 px-4 py-3 transition-colors ${
                  active
                    ? "bg-[hsl(var(--primary-soft))]"
                    : unread
                    ? "bg-[hsl(var(--primary-soft))]/40 hover:bg-[hsl(var(--primary-soft))]/60"
                    : "hover:bg-muted"
                }`}
                data-testid={`chat-row-${t.peer.username}`}
              >
                <div className="w-12 h-12 rounded-full bg-muted overflow-hidden flex items-center justify-center shrink-0">
                  {t.peer.profile_photo ? (
                    <img src={fileUrl(t.peer.profile_photo)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-lg font-bold text-[hsl(var(--primary))]">{t.peer.username?.[0]?.toUpperCase()}</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-semibold truncate flex items-center gap-1">
                      @{t.peer.username}
                      {t.peer.verification_status === "verified" && (
                        <BadgeCheck className="w-3.5 h-3.5 fill-[hsl(var(--primary))] text-white" />
                      )}
                    </div>
                    <div className="text-[11px] text-muted-foreground shrink-0">{timeAgo(t.updated_at)}</div>
                  </div>
                  <div className={`text-xs truncate ${unread ? "font-semibold text-foreground" : "text-muted-foreground"}`}>
                    {t.last_message || "Say hi 👋"}
                  </div>
                </div>
                {unread > 0 && (
                  <span
                    className="min-w-[22px] h-[22px] px-1.5 rounded-full bg-[hsl(var(--primary))] text-white text-[11px] font-bold flex items-center justify-center"
                    data-testid={`chat-unread-${t.peer.username}`}
                  >{unread}</span>
                )}
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
}
